import keheLogo from "/img/integration/kehe.png"
import { Form } from "@/components/form/Form.tsx"
import { useFormState } from "@/components/form/form_state.tsx"
import { Input } from "@/components/form/Input.tsx"
import { Button } from "@/components/ui/button.tsx"
import { Checkbox } from "@/components/ui/checkbox.tsx"
import { Label } from "@/components/ui/label.tsx"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select.tsx"
import { useState } from "preact/compat"
import { useLocation } from "wouter-preact"
import { CenterColumnContainer } from "@/components/LoginScreen.tsx"
import { LoadingSpinner } from "@/app/loading"
import { useAsyncEffect } from "@/utils"
import { LedgerAccount } from "@/api"
import { api_fetch } from "@/api/client"
import { getIntegrations, updateIntegration, Integration, KeheConfig } from "@/api/integration"
import { LedgerAccountSelect } from "@/components/ledger_account_select"
import { toast } from "@/components/ui/use-toast"

interface KeheConfigFormProps {
  integration: Integration
  onSuccess: (int: Integration) => void
}

interface KeheConfigFormFields {
  discount_term: string
  discount_percentage: string
  use_spoilage_allowance: boolean
  bi_fee_proactive_discount: boolean
  bi_fee_percentage: string
  bi_allowance_percentage: string
  aap_fee_proactive_discount: boolean
  aap_fee_percentage: string
  delivery_method: string
}

export function KeheConfigForm({ integration, onSuccess }: KeheConfigFormProps) {
  const [loading, setLoading] = useState(false)
  const [accounts, setAccounts] = useState<LedgerAccount[]>([])

  const data = integration.data as unknown as KeheConfig
  const [variance_account, setVarianceAccount] = useState<string | null>(data.ia_variance_account ?? null)
  const [spoilage_account, setSpoilageAccount] = useState<string | null>(data.ia_spoilage_allowance_account ?? null)
  const [off_invoice_account, setOffInvoiceAccount] = useState<string | null>(data.ia_off_invoice_account ?? null)
  const [allowance_account, setAllowanceAccount] = useState<string | null>(data.ia_allowance_account ?? null)
  const [bi_allowance_account, setBiAllowanceAccount] = useState<string | null>(data.bi_allowance_account ?? null)

  useAsyncEffect(async () => {
    const res = await api_fetch<LedgerAccount[]>("/reason_codes")
    if (res.ok) {
      setAccounts(res.value.data)
    }
  }, [])
  const form = useFormState<KeheConfigFormFields>({
    discount_term: data.discount_term?.toString() || "",
    discount_percentage: data.discount_percentage?.toString() || "",
    use_spoilage_allowance: data.use_spoilage_allowance || false,
    bi_fee_proactive_discount: data.bi_fee_proactive_discount || false,
    bi_fee_percentage: data.bi_fee_percentage?.toString() || "",
    bi_allowance_percentage: data.bi_allowance_percentage?.toString() || "",
    aap_fee_proactive_discount: data.aap_fee_proactive_discount || false,
    aap_fee_percentage: data.aap_fee_percentage?.toString() || "",
    delivery_method: data.delivery_method || "",
  })

  async function onSubmit() {
    setLoading(true)
    form.set_form_errors([])
    let discountTerm = parseInt(form.discount_term.value, 10)
    let discountPercentage = parseFloat(form.discount_percentage.value)
    let biFeePercentage = parseFloat(form.bi_fee_percentage.value)
    let aapFeePercentage = parseFloat(form.aap_fee_percentage.value)
    const bi_allowance_percentage = parseFloat(form.bi_allowance_percentage.value)
    let config: KeheConfig = {
      discount_term: isNaN(discountTerm) ? null : discountTerm,
      discount_percentage: isNaN(discountPercentage) ? null : discountPercentage,
      ia_variance_account: variance_account,
      ia_spoilage_allowance_account: spoilage_account,
      ia_off_invoice_account: off_invoice_account,
      ia_allowance_account: allowance_account,
      use_spoilage_allowance: form.use_spoilage_allowance.value,
      bi_fee_proactive_discount: form.bi_fee_proactive_discount.value,
      bi_fee_percentage: isNaN(biFeePercentage) ? null : biFeePercentage,
      bi_allowance_account: bi_allowance_account,
      bi_allowance_percentage: isNaN(bi_allowance_percentage) ? null : bi_allowance_percentage,
      aap_fee_proactive_discount: form.aap_fee_proactive_discount.value,
      aap_fee_percentage: isNaN(aapFeePercentage) ? null : aapFeePercentage,
      delivery_method: form.delivery_method.value || null,
      email_address: data.email_address ?? null,
      dispute_from_full_name: data.dispute_from_full_name ?? null,
    }
    const res = await updateIntegration(integration.id, { data: config })
    setLoading(false)
    if (!res.ok) {
      form.set_form_errors(["Failed to save configuration. Please try again."])
      return
    }
    toast({
      title: "Settings saved",
      description: "Your KeHE configuration has been updated.",
    })
    onSuccess(res.value.data)
  }

  return (
    <div>
      <Form class="space-y-6" onSubmit={onSubmit} alerts={form.form_alerts}>
        <Input
          label="Discount Term (days)"
          type="number"
          name="discount_term"
          required={true}
          bind={form.discount_term}
        />

        <Input
          label="Early Pay Discount (%)"
          type="number"
          name="discount_percentage"
          placeholder="2.00"
          step="0.01"
          bind={form.discount_percentage}
        />

        <div class="space-y-3 border-t border-gray-200 pt-5">
          <div>
            <h3 class="text-sm font-medium text-gray-900">Invoice Adjustment coding</h3>
            <p class="text-xs text-gray-500">
              KeHE numbers every adjustment the same way (<code>170708IA</code>), whether it is a receiving shortage or a
              recalculated allowance, so MarginWiz reads the adjustment form itself and codes it from what it says. Leave an
              account unset to keep coding those by hand.
            </p>
          </div>

          <div class="space-y-1">
            <Label htmlFor="ia_variance_account">Shortage or price variance</Label>
            <LedgerAccountSelect
              id="ia_variance_account"
              value={variance_account}
              onValueChange={setVarianceAccount}
              accounts={accounts}
              none_label="Not set — code these by hand"
            />
            <p class="text-xs text-gray-500">
              The adjustment shows short or mispriced product. Most brands point this at their quantity-discrepancy account. The
              whole adjustment codes here, including the allowance lines that follow the goods back, and an adjustment listing
              several products splits into one line each.
            </p>
          </div>

          <div class="space-y-1">
            <Label htmlFor="ia_off_invoice_account">Off-invoice / billback only</Label>
            <LedgerAccountSelect
              id="ia_off_invoice_account"
              value={off_invoice_account}
              onValueChange={setOffInvoiceAccount}
              accounts={accounts}
              none_label="Not set — code these by hand"
            />
            <p class="text-xs text-gray-500">
              The goods arrived fine, but the invoice went out without the off-invoice allowance the PO carried, so KeHE took the
              difference. This is deal money you agreed to, so it usually belongs with promotions rather than with shortages.
            </p>
          </div>

          <div class="space-y-1">
            <Label htmlFor="ia_allowance_account">Billing allowance only</Label>
            <LedgerAccountSelect
              id="ia_allowance_account"
              value={allowance_account}
              onValueChange={setAllowanceAccount}
              accounts={accounts}
              none_label="Not set — code these by hand"
            />
            <p class="text-xs text-gray-500">
              Nothing but a recalculated billing or marketing allowance — KeHE's CONNECT BI allowance. A few cents for some
              brands and hundreds of dollars for others, so check yours before leaving it unset.
            </p>
          </div>

          <div class="space-y-1">
            <Label htmlFor="ia_spoilage_allowance_account">Spoilage allowance only</Label>
            <LedgerAccountSelect
              id="ia_spoilage_allowance_account"
              value={spoilage_account}
              onValueChange={setSpoilageAccount}
              accounts={accounts}
              none_label="Not set — code these by hand"
            />
            <p class="text-xs text-gray-500">
              Nothing on the adjustment but KeHE recalculating your spoilage allowance — no shortage, no price difference. Point
              this at a spoilage-allowance account where you book it separately; leave it unset to keep it with rejected product.
            </p>
          </div>
        </div>

        <div class="space-y-3 border-t border-gray-200 pt-5">
          <div>
            <h3 class="text-sm font-medium text-gray-900">BI allowance write-off</h3>
            <p class="text-xs text-gray-500">
              KeHE pays every invoice short by its BI allowance and never lists it on the remittance, so without this each
              invoice is left open by that amount. With an account set, Apply Cash writes each invoice's BI-sized short off
              to it on the credit memo. A short wider than the allowance books the allowance and flags the rest.
            </p>
          </div>

          <div class="space-y-1">
            <Label htmlFor="bi_allowance_account">BI allowance account</Label>
            <LedgerAccountSelect
              id="bi_allowance_account"
              value={bi_allowance_account}
              onValueChange={setBiAllowanceAccount}
              accounts={accounts}
              none_label="Not set — leave the short open"
            />
          </div>

          {bi_allowance_account ? (
            <Input
              label="BI allowance (% of invoice)"
              type="number"
              name="bi_allowance_percentage"
              placeholder="2.00"
              step="0.01"
              bind={form.bi_allowance_percentage}
            />
          ) : null}
        </div>

        <div class="flex items-center space-x-3">
          <Checkbox
            id="use_spoilage_allowance"
            checked={form.use_spoilage_allowance.value}
            onCheckedChange={checked => {
              form.set_form_data({ use_spoilage_allowance: checked === true })
            }}
          />
          <Label htmlFor="use_spoilage_allowance" class="text-sm font-medium text-gray-700">
            Use spoilage allowance
          </Label>
        </div>

        <div class="space-y-2">
          <div class="flex items-center space-x-3">
            <Checkbox
              id="bi_fee_proactive_discount"
              checked={form.bi_fee_proactive_discount.value}
              onCheckedChange={checked => {
                form.set_form_data({ bi_fee_proactive_discount: checked === true })
              }}
            />
            <Label htmlFor="bi_fee_proactive_discount" class="text-sm font-medium text-gray-700">
              Proactively give BI fee as discount on invoices
            </Label>
          </div>
          {form.bi_fee_proactive_discount.value && (
            <div class="ml-7">
              <Input
                label="BI Fee Percentage"
                type="number"
                name="bi_fee_percentage"
                placeholder="0.00"
                step="0.01"
                bind={form.bi_fee_percentage}
              />
            </div>
          )}
        </div>
        <div class="space-y-4">
          <div class="space-y-2">
            <div class="flex items-center space-x-3">
              <Checkbox
                id="aap_fee_proactive_discount"
                checked={form.aap_fee_proactive_discount.value}
                onCheckedChange={checked => {
                  form.set_form_data({ aap_fee_proactive_discount: checked === true })
                }}
              />
              <Label htmlFor="aap_fee_proactive_discount" class="text-sm font-medium text-gray-700">
                Proactively give AAP fee as discount on invoices
              </Label>
            </div>
            {form.aap_fee_proactive_discount.value && (
              <div class="ml-7">
                <Input
                  label="AAP Fee Percentage"
                  type="number"
                  name="aap_fee_percentage"
                  placeholder="0.00"
                  step="0.01"
                  bind={form.aap_fee_percentage}
                />
              </div>
            )}
          </div>
        </div>

        <div class="space-y-2">
          <Label htmlFor="delivery_method">Delivery Method</Label>
          <Select
            value={form.delivery_method.value}
            onValueChange={value => {
              form.set_form_data({ delivery_method: value })
            }}>
            <SelectTrigger id="delivery_method">
              <SelectValue placeholder="Select delivery method..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="FOB">Customer Pickup</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <Button
          type="submit"
          class="w-full text-white bg-kehe-500 rounded-md hover:bg-kehe-600 focus:outline-none focus:ring-1 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={loading}>
          Save Configuration
        </Button>
      </Form>
    </div>
  )
}

export function ConfigKehePage() {
  const [, setLocation] = useLocation()
  const [integration, setIntegration] = useState<Integration | null>(null)
  const [loading, setLoading] = useState(true)

  useAsyncEffect(async () => {
    // First find the kehe integration to get its ID
    const res = await getIntegrations({ name: "kehe" })
    if (res.ok && res.value.data.length > 0) {
      setIntegration(res.value.data[0])
    }
    setLoading(false)
  }, [])

  if (loading) {
    return (
      <CenterColumnContainer title="">
        <img src={keheLogo} alt="KeHE" />
        <div class="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      </CenterColumnContainer>
    )
  }

  if (!integration) {
    return (
      <CenterColumnContainer title="">
        <img src={keheLogo} alt="KeHE" />
        <div class="text-center py-8">
          <p class="text-gray-500 mb-4">No KeHE integration found. Please connect KeHE first.</p>
          <Button
            class="bg-gray-900 text-white rounded-lg hover:bg-gray-800"
            onClick={() => setLocation("/setup/connect/kehe")}>
            Connect KeHE
          </Button>
        </div>
      </CenterColumnContainer>
    )
  }

  return (
    <CenterColumnContainer title="">
      <img src={keheLogo} alt="KeHE" />
      <KeheConfigForm integration={integration} onSuccess={() => setLocation("/setup/connect")} />
    </CenterColumnContainer>
  )
}
